import _ from 'lodash';
import dayjs from '../../../utils/dayjs';
import { buildRegionPayload, parseRegionPayload } from '../static/campaignUtils';
import {
  DATE_FORMAT,
  DRAFT_TIMEZONE_TO_IANA,
  SOCIAL_LINK_FIELDS,
} from './static/campaignWizardConstants';

// The `detailsData` keys the wizard edits; every other stored key is left as-is on edit.
const WIZARD_DETAILS_KEYS = [
  'campaignGoals',
  'campaignObjectives',
  'objectiveDetails',
  'creatorsAmount',
  'ftcLink',
  'ftcAssets',
  'ftcAssetsSubmited',
  'COPPA',
  'alcohol',
  'pharmaISI',
];

// Generated values the wizard doesn't edit, carried from the draft into the create payload
// where the old create form stores them.
const DRAFT_CAMPAIGN_DETAILS_KEYS = [
  'productName',
  'tiktokDeliverables',
  'instagramDeliverables',
  'youtubeDeliverables',
];
const DRAFT_DETAILS_DATA_KEYS = [
  'successDefinition',
  'priceRange_min',
  'priceRange_max',
  'videoDuration_min',
  'videoDuration_max',
  'briefMusicNeeded',
  'briefCreatorShouldDo',
  'briefCreatorShouldAvoid',
  'briefTechRequiroments',
  'briefCaptions',
  'briefHashtags',
  'termsOrganic',
  'termsPaid',
  'brandSafetyFlagSetting',
  'CTA',
];
const toDayjs = (value) => {
  if (!value) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : undefined;
};

// Stored detail columns are JSON strings (or already-parsed objects).
const parseJSON = (value, fallback) => {
  if (value && typeof value === 'object') return value;
  try {
    return JSON.parse(value) ?? fallback;
  } catch (e) {
    return fallback;
  }
};

// `{ instagram, … }` social links → the form's `brand*Link` fields.
const socialLinksToFields = (links) =>
  Object.fromEntries(SOCIAL_LINK_FIELDS.map(({ network, name }) => [name, links?.[network]]));

// The form's `brand*Link` fields → `{ instagram, … }` social links.
const fieldsToSocialLinks = (values) =>
  Object.fromEntries(SOCIAL_LINK_FIELDS.map(({ network, name }) => [network, values[name] || '']));

/**
 * Generated draft → antd form values. The flight dates become a `[dayjs, dayjs]` range,
 * the generated timezone label becomes its IANA zone,
 * and the stored `region` composite ("US-California") splits into `country` + `region`.
 */
export const draftToFormValues = (draft) => {
  if (!draft) return {};
  const { flight_start: flightStart, flight_end: flightEnd, ...rest } = draft;
  const start = toDayjs(flightStart);
  const end = toDayjs(flightEnd);
  return {
    ...rest,
    flight_tz: DRAFT_TIMEZONE_TO_IANA[rest.flight_tz] || rest.flight_tz,
    ...(!rest.country && rest.region ? parseRegionPayload(rest.region) : {}),
    flight_dates: start && end ? [start, end] : undefined,
  };
};

/**
 * Stored campaign (`/campaign/get-overview`: `{ campaign, campaignDetails,
 * campaignBrandDetails }`) → antd form values, for editing.
 */
export const campaignToFormValues = (existing) => {
  if (!existing?.campaign) return {};
  const { campaign, campaignDetails, campaignBrandDetails } = existing;
  const details = campaignDetails || {};
  const budget = Number(campaign.budget);

  return draftToFormValues({
    name: campaign.name,
    flight_start: campaign.flight_start,
    flight_end: campaign.flight_end,
    flight_tz: campaign.flight_tz,
    budget: Number.isFinite(budget) ? budget : undefined,
    region: details.region,
    campaignDescription: details.campaignDescription,
    ..._.pick(parseJSON(details.detailsData, {}), WIZARD_DETAILS_KEYS),
    ...socialLinksToFields(parseJSON(campaignBrandDetails?.socialMediaLinks, {})),
  });
};

/**
 * Wizard form values → the create / save payload.
 * Creates go to the run's `POST /campaign/wizard/runs/{id}/campaign` (create-v2 shape, with
 * the nested details as objects). They combine the form with the generated `draft` (for what
 * the form doesn't edit) and the reviewed brand info, which wins over the generated brand
 * details.
 * Edits go to `/campaign/createV2` (nested details as JSON strings, as `CreateCampaignV2`
 * sends them). They pass the stored campaign as `existing` and only overwrite what the
 * wizard edits.
 */
export const buildCampaignPayload = (values, { brandInfo, draft, existing } = {}) => {
  const [start, end] = values.flight_dates || [];

  const wizardDetails = {
    ..._.pick(values, WIZARD_DETAILS_KEYS),
    COPPA: !!values.COPPA,
    alcohol: !!values.alcohol,
    pharmaISI: !!values.pharmaISI,
  };

  const socialMediaLinks = fieldsToSocialLinks(values);
  const payload = {
    name: values.name,
    flight_start: start ? start.format(DATE_FORMAT) : undefined,
    flight_end: end ? end.format(DATE_FORMAT) : undefined,
    timezone: values.flight_tz,
    budget: values.budget,
  };

  if (existing?.campaign) {
    const { campaign, campaignDetails, campaignBrandDetails } = existing;
    return {
      ...payload,
      id: campaign.id,
      status: campaign.status,
      // The BE replaces `detailsData` wholesale but skips omitted columns (deliverables,
      // creator brief, …), so merge into the stored details rather than replace them.
      campaignDetails: JSON.stringify({
        region: buildRegionPayload(values.country, values.region),
        campaignDescription: values.campaignDescription,
        detailsData: { ...parseJSON(campaignDetails?.detailsData, {}), ...wizardDetails },
      }),
      // Brand details only update the keys sent; name and description also feed TTC_campaign.
      campaignBrandDetails: JSON.stringify({
        ..._.pick(campaignBrandDetails || {}, ['brandName', 'brandDescription']),
        socialMediaLinks,
      }),
    };
  }

  const generated = draft || {};
  const brandURL = brandInfo?.brandURL || generated.brandURL;

  return {
    ...payload,
    status: 'Pending',
    campaignDetails: {
      ..._.pick(generated, DRAFT_CAMPAIGN_DETAILS_KEYS),
      region: buildRegionPayload(values.country, values.region),
      campaignDescription: values.campaignDescription,
      productDescription: generated.productDescription,
      detailsData: {
        ..._.pick(generated, DRAFT_DETAILS_DATA_KEYS),
        ...wizardDetails,
        brandURL,
      },
    },
    campaignBrandDetails: {
      brandName: brandInfo?.brandName || generated.brandName,
      brandURL,
      brandDescription: generated.brandDescription,
      socialMediaLinks,
      competitorData: generated.competitorData,
      brandIndustry: generated.brandIndustry?.categoryName,
    },
  };
};
